import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SHOP_CANCELLABLE_STATUSES,
  SHOP_DELIVERY_METHOD_LABELS_RU,
  SHOP_ORDER_STATUS_LABELS_RU_ADMIN,
  type ShopDeliveryMethod,
  type ShopOrderStatus,
} from "@mnp/shared";
import { api } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { shortId, formatGel, formatDateTime, telegramHref } from "../../lib/orders";
import { useOptionalRole } from "../../lib/role.tsx";
import { hasPermission } from "@mnp/shared";

type ShopOrderDetail = {
  id: string;
  chatId: string;
  status: string;
  qrCodePickup: string | null;
  deliveryType: string | null;
  paymentMethod?: "on_pickup" | "remote" | null;
  // «Свой курьер» (deliveryType=self_courier): ссылка на отслеживание Яндекс.Доставки.
  trackingUrl?: string | null;
  totalGel: number | null;
  createdAt: string;
  items: Array<{ name: string; quantity?: number; qty?: number }>;
  user?: {
    chatId: string;
    username: string | null;
    tgUsername?: string | null;
    fullName: string | null;
    phone: string | null;
  };
  // Review-цикл (2026-05-29). Сервер отдаёт эти поля при /admin/orders/:id
  // для shop-заказов (см. миграцию 20260529a и serializeOrder).
  rejectedAt?: string | null;
  rejectedReason?: string | null;
};

const SHOP_STATUS_BADGE: Record<ShopOrderStatus, string> = {
  pending: "gray",
  in_review: "orange",
  rejected: "red",
  awaiting_payment: "yellow",
  paid: "blue",
  ready: "teal",
  completed: "green",
  cancelled: "red",
  refused: "red",
};

/**
 * Minimal read-only drawer body for shop orders. The full shop fulfilment
 * surface is a later epic; this gives the unified order list a clean shop
 * view without the Ozon/WB-specific controls.
 *
 * Review-цикл (2026-05-29): pending/in_review/rejected админ обрабатывает
 * через кнопки «Принять»/«Отклонить» в шапке; rejected показывает баннер
 * с причиной от предыдущего отказа.
 */
export function ShopBody({ id, onClose }: { id: string; onClose: () => void }) {
  // Гейт по фактическому праву orders:status (см. комментарий в FlexBody):
  // approve/reject/cancel требуют его на API — UI совпадает с API для всех
  // ролей. Дефолт вне RoleProvider — 'chats': fail-closed.
  const role = useOptionalRole();
  const canMutate = hasPermission(role, "orders:status");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-order", id],
    queryFn: () => api<ShopOrderDetail>(`/admin/orders/${id}`),
  });

  const approve = useMutation({
    mutationFn: () => api(`/admin/orders/${id}/approve`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api(`/admin/orders/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });

  // Отмена заказа (карточка shop-cancel-and-reminders §4.12): доступна
  // только до оплаты; из paid сервер отвечает 409.
  const cancelOrder = useMutation({
    mutationFn: (reason: string) =>
      api(`/admin/orders/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);

  function submitReject() {
    setRejectError(null);
    const trimmed = rejectReason.trim();
    if (trimmed.length < 5) {
      setRejectError("Причина должна быть не короче 5 символов.");
      return;
    }
    reject.mutate(trimmed, {
      onSuccess: () => {
        setRejectOpen(false);
        setRejectReason("");
      },
      onError: (err) => {
        const e = err as { body?: { message?: string }; message?: string };
        setRejectError(e?.body?.message ?? e?.message ?? "Не удалось отклонить");
      },
    });
  }

  function submitCancel() {
    setCancelError(null);
    const trimmed = cancelReason.trim();
    if (trimmed.length < 3) {
      setCancelError("Причина должна быть не короче 3 символов.");
      return;
    }
    cancelOrder.mutate(trimmed, {
      onSuccess: () => {
        setCancelOpen(false);
        setCancelReason("");
      },
      onError: (err) => {
        const e = err as { body?: { message?: string }; message?: string };
        setCancelError(e?.body?.message ?? e?.message ?? "Не удалось отменить заказ");
      },
    });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isInReview = data?.status === "in_review";
  const isRejected = data?.status === "rejected";
  // Отмена только до оплаты (paid запрещён — деньги получены, возврата нет).
  // Набор статусов — единый shared-источник SHOP_CANCELLABLE_STATUSES,
  // тот же, что энфорсит cancelShopOrder на API (карточка shop-status-flow-shared).
  const isCancellable =
    data != null &&
    (SHOP_CANCELLABLE_STATUSES as readonly string[]).includes(data.status);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <button className="btn-admin ghost" aria-label="Закрыть заказ" onClick={onClose}>
            <Icon name="close" />
          </button>
          {!data ? (
            <h2>{isLoading ? "Загрузка…" : "Заказ магазина"}</h2>
          ) : (
            <>
              <h2>{data.qrCodePickup || shortId(data.id)}</h2>
              <span className={"badge-a " + (SHOP_STATUS_BADGE[data.status as ShopOrderStatus] ?? "gray")}>
                Магазин ·{" "}
                {SHOP_ORDER_STATUS_LABELS_RU_ADMIN[data.status as ShopOrderStatus] ??
                  data.status}
              </span>
              <div className="grow" />
              {!canMutate && (
                // Read-only роль: ни одной мутационной кнопки (они дали бы 403).
                <span
                  style={{ color: "var(--mnp-fg-3)", fontSize: 12 }}
                  data-testid="shop-readonly"
                >
                  Режим просмотра
                </span>
              )}
              {canMutate && isInReview && (
                <>
                  <button
                    className="btn-admin ghost"
                    onClick={() => {
                      setRejectError(null);
                      setRejectReason("");
                      setRejectOpen(true);
                    }}
                    disabled={reject.isPending || approve.isPending}
                    data-testid="shop-reject-open"
                  >
                    Отклонить
                  </button>
                  <button
                    className="btn-admin primary"
                    onClick={() => approve.mutate()}
                    disabled={approve.isPending || reject.isPending}
                    data-testid="shop-approve"
                  >
                    {approve.isPending ? "…" : "Принять в работу"}
                  </button>
                </>
              )}
              {canMutate && isCancellable && (
                <button
                  className="btn-admin ghost"
                  style={{ color: "var(--mnp-red, #c00)" }}
                  onClick={() => {
                    setCancelError(null);
                    setCancelReason("");
                    setCancelOpen(true);
                  }}
                  disabled={cancelOrder.isPending || reject.isPending || approve.isPending}
                  data-testid="shop-cancel-open"
                >
                  Отменить заказ
                </button>
              )}
              {isRejected && (
                <span style={{ color: "var(--mnp-fg-3)", fontSize: 12 }}>
                  Ждём повторной отправки
                </span>
              )}
            </>
          )}
        </div>

        {data && (
          <div className="drawer-body">
            {isRejected && data.rejectedReason && (
              <div
                data-testid="shop-rejected-banner"
                style={{
                  background: "#FDECED",
                  border: "1px solid #F5B5B7",
                  borderLeft: "3px solid var(--mnp-red, #c00)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 14,
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: "var(--mnp-red-dark, #a40000)",
                }}
              >
                <b style={{ display: "block", marginBottom: 4 }}>Отклонено</b>
                <span style={{ whiteSpace: "pre-wrap" }}>{data.rejectedReason}</span>
                {data.rejectedAt && (
                  <div style={{ marginTop: 6, color: "var(--mnp-fg-3)", fontSize: 11 }}>
                    {formatDateTime(data.rejectedAt)}
                  </div>
                )}
              </div>
            )}
            <p className="eyebrow-a">Клиент</p>
            <dl className="inline-grid">
              <div>
                <dt>ФИО</dt>
                <dd>{data.user?.fullName ?? "—"}</dd>
              </div>
              <div>
                <dt>Телефон</dt>
                <dd>{data.user?.phone ?? "—"}</dd>
              </div>
              <div>
                <dt>Telegram</dt>
                <dd>
                  <a
                    className="tg-deeplink"
                    href={telegramHref(data.user?.tgUsername ?? data.user?.username, data.chatId)}
                    target="_blank"
                    rel="noreferrer"
                    title="Открыть Telegram клиента"
                  >
                    <span className="tg-deeplink-ico">TG</span>
                    {data.user?.username ? `@${data.user.username}` : "Открыть профиль ↗"}
                  </a>
                </dd>
              </div>
              <div>
                <dt>Оплата</dt>
                <dd>{data.paymentMethod ?? "—"}</dd>
              </div>
            </dl>

            <p className="eyebrow-a">Доставка</p>
            <dl className="inline-grid">
              <div>
                <dt>Метод</dt>
                <dd data-testid="shop-delivery-method">
                  {data.deliveryType
                    ? SHOP_DELIVERY_METHOD_LABELS_RU[data.deliveryType as ShopDeliveryMethod] ??
                      data.deliveryType
                    : "—"}
                </dd>
              </div>
              {data.deliveryType === "self_courier" && (
                <div>
                  <dt>Отслеживание</dt>
                  <dd>
                    {data.trackingUrl ? (
                      <a
                        href={data.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="shop-tracking-link"
                        title="Открыть отслеживание курьера"
                      >
                        Открыть трекинг ↗
                      </a>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              )}
            </dl>

            <p className="eyebrow-a">Товары · {data.items?.length ?? 0} шт</p>
            <div className="item-list">
              {data.items?.length ? (
                data.items.map((it, i) => (
                  <div className="row" key={i}>
                    <div className="grow">
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{it.name}</div>
                      <div className="muted" style={{ fontSize: 11 }}>
                        × {it.quantity ?? it.qty ?? 1}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="muted" style={{ padding: 8 }}>
                  Товары не указаны
                </div>
              )}
            </div>

            <p className="eyebrow-a">Расчёт</p>
            <div
              style={{
                padding: "12px 14px",
                background: "var(--mnp-teal-50)",
                borderRadius: 8,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 4,
              }}
            >
              <span className="bold">Итого к оплате</span>
              <span style={{ fontFamily: "Montserrat", fontWeight: 800, fontSize: 18 }}>
                {formatGel(data.totalGel)}
              </span>
            </div>

            <p className="eyebrow-a">Создан</p>
            <div style={{ fontSize: 12, color: "var(--mnp-fg-2)" }}>
              {formatDateTime(data.createdAt)}
            </div>
          </div>
        )}
        {rejectOpen && (
          <div
            data-testid="shop-reject-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              padding: 20,
            }}
          >
            <div
              style={{
                background: "var(--mnp-bg, #fff)",
                borderRadius: 12,
                padding: 20,
                width: "min(480px, 100%)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Отклонить заявку</h3>
              <p style={{ color: "var(--mnp-fg-2)", fontSize: 12, marginTop: 0 }}>
                Опишите, что нужно исправить — текст уйдёт клиенту в Telegram.
              </p>
              <textarea
                data-testid="shop-reject-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={5}
                maxLength={2000}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--mnp-border, #cbd5e1)",
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
                placeholder="Например: нет в наличии часть товаров, уточните доставку…"
              />
              {rejectError && (
                <p
                  style={{ color: "var(--mnp-red, #c00)", fontSize: 12, marginTop: 6 }}
                  data-testid="shop-reject-error"
                >
                  {rejectError}
                </p>
              )}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}
              >
                <button
                  className="btn-admin ghost"
                  onClick={() => setRejectOpen(false)}
                  disabled={reject.isPending}
                >
                  Отменить
                </button>
                <button
                  className="btn-admin primary"
                  onClick={submitReject}
                  disabled={reject.isPending}
                  data-testid="shop-reject-submit"
                >
                  {reject.isPending ? "Отправка…" : "Отклонить и уведомить"}
                </button>
              </div>
            </div>
          </div>
        )}
        {cancelOpen && (
          <div
            data-testid="shop-cancel-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              padding: 20,
            }}
          >
            <div
              style={{
                background: "var(--mnp-bg, #fff)",
                borderRadius: 12,
                padding: 20,
                width: "min(480px, 100%)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Отменить заказ</h3>
              <p style={{ color: "var(--mnp-fg-2)", fontSize: 12, marginTop: 0 }}>
                Укажите причину отмены — текст уйдёт клиенту в Telegram. Заказ
                нельзя будет восстановить.
              </p>
              <textarea
                data-testid="shop-cancel-textarea"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={5}
                maxLength={500}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--mnp-border, #cbd5e1)",
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
                placeholder="Например: нет в наличии, клиент передумал…"
              />
              {cancelError && (
                <p
                  style={{ color: "var(--mnp-red, #c00)", fontSize: 12, marginTop: 6 }}
                  data-testid="shop-cancel-error"
                >
                  {cancelError}
                </p>
              )}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}
              >
                <button
                  className="btn-admin ghost"
                  onClick={() => setCancelOpen(false)}
                  disabled={cancelOrder.isPending}
                >
                  Назад
                </button>
                <button
                  className="btn-admin primary"
                  onClick={submitCancel}
                  disabled={cancelOrder.isPending}
                  data-testid="shop-cancel-submit"
                >
                  {cancelOrder.isPending ? "Отправка…" : "Отменить и уведомить"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
